import { call, put } from 'redux-saga/effects'
import { initialize } from 'redux-form'
import get from 'lodash/get'
import { PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'

import { Action, AvailabilityResponse, ExtendedResponse } from '../../types'
import { appActions, modalDialogActions, wizardActions } from '../../redux'
import { ERRORS } from '../../constants/errors'
import { OpenApiInterface } from '../../api/requests/openApi'
import { Response } from '../../api/api'
import { reloadPage } from '../../utils/url'
import { saveFlowToStorage, removeFlowFromStorage } from '../../utils/flowId'
import { AvailabilityStatus, NOT_AVAILABLE, Routes, URL } from '../../constants'
import * as errorModals from '../../constants/errorModals'
import mockReduxState from '../../redux/mockReduxState'
import { eventLogger } from '../../utils'
import { ResponseError } from '../../utils/errors'

export function* initWizard(): Generator {
  // If referrer is not available (e.g. Internet Explorer does not support it) us pre-defined url
  const referrerUrl = document.referrer || URL.MONETA_LOAN
  yield put(wizardActions.setReferrerUrl(referrerUrl))
}

export function* showCentralErrorModal(action: PayloadAction<ERRORS>): Generator {
  const errorCode: ERRORS = action.payload

  let modalConfig: any = {}

  switch (errorCode) {
    case ERRORS.NETWORK:
      modalConfig = errorModals.NETWORK
      break

    case ERRORS.UNAUTHORIZED:
      modalConfig = errorModals.UNAUTHORIZED
      break

    case ERRORS.SERVER:
      modalConfig = errorModals.SERVER
      modalConfig.closeButton = {
        onClick: reloadPage,
      }
      break
    case ERRORS.SERVER_RETRY:
      modalConfig = errorModals.SERVER_RETRY
      break

    default:
      modalConfig = errorModals.TECHNICAL_DIFFICULTIES
      modalConfig.closeButton = {
        onClick: reloadPage,
      }
      break
  }

  yield put(modalDialogActions.showModalDialog(modalConfig))
}

export function* prefillForm(action: PayloadAction<string>): Generator {
  const formName: string = action.payload

  yield put(initialize(formName, get(mockReduxState, `form.${formName}.values`)))
}

export function* getSaveReturnUrl(api: OpenApiInterface): Generator {
  try {
    yield put(appActions.setSessionExpired())
    const response = (yield call(api.getSaveReturnUrl)) as ExtendedResponse

    if (response.responseStatus === Response.ok) {
      const saveReturnUrl: string = response.data?.saveReturnUrl || NOT_AVAILABLE
      yield put(appActions.setSaveReturnUrl(saveReturnUrl))
    }
  } catch (e) {
    // just be silent... we are on error exit page already.
  }
}

export function* resetApplication() {
  yield removeFlowFromStorage()
  yield reloadPage()
}

export function* setFlowId(action: Action<string>) {
  yield saveFlowToStorage(action.payload)
  yield eventLogger.setFlowId(action.payload)
}

export function* fetchAvailability(api: OpenApiInterface): Generator {
  const response = (yield call(api.getAvailability)) as ExtendedResponse<AvailabilityResponse>

  if (response.responseStatus === Response.ok) {
    if (response.data.status !== 'OK') {
      yield put(appActions.setAvailability(response.data))
    }
    if (response.data.status === AvailabilityStatus.OngoingOutage) {
      yield put(push(Routes.AVAILABILITY))
      yield put(appActions.setAppReady())
      return false
    }
    return true
  }
  throw new ResponseError('app fetchAvailability saga', response)
}
